import { useEffect, useLayoutEffect, useRef } from 'react';
import { validateFiles } from '../../api/emailsApi/files';
import { setFileValidationErrors, setFileValidationPending } from '../actions/files';
import { logError } from './request';

const VALIDATION_DEBOUNCE_MS = 500;

/**
 * Builds the neutral candidate payload for the pre-upload dry-run. One candidate is produced per
 * (file, related record) save location, since the real upload creates one File record per pairing.
 * The temporary client-side file id is kept only in a parallel array so violations (returned by
 * index) can be mapped back to their row - it is never sent to the server.
 *
 * Explicitly selected save locations (file.folderPaths) are always validated. When an auto-save
 * setting is on (save-all attachments / save-all emails, as used by auto-threading), the file will
 * be uploaded to every selected record even without an explicit selection, so we also generate a
 * candidate per selected record using that record type's default folder path.
 * @param {Array<object>} files - docrioFiles from redux
 * @param {object} selectedRecords - selectedRecords map from redux
 * @param {object} [options]
 * @param {boolean} [options.saveAllAttachments] - attachments are auto-saved to every record
 * @param {boolean} [options.saveAllEmails] - the email file is auto-saved to every record
 * @param {object} [options.defaultFolderSettings] - per-record-type default folder paths
 * @param {Set<string>|string[]} [options.emailFileIds] - ids that are the email (.eml) file
 * @return {{candidates: Array<object>, fileIds: string[]}}
 */
export const buildFileValidationCandidates = (files = [], selectedRecords = {}, options = {}) => {
  const {
    saveAllAttachments = false,
    saveAllEmails = false,
    defaultFolderSettings = {},
    emailFileIds,
  } = options;
  const emailIds = emailFileIds instanceof Set ? emailFileIds : new Set(emailFileIds || []);

  const candidates = [];
  const fileIds = [];

  files.forEach((file) => {
    const isEmailFile = emailIds.has(file.id);
    const autoSave = isEmailFile ? saveAllEmails : saveAllAttachments;
    const folderPaths = file?.folderPaths || {};

    // Validate every record this file will actually be uploaded to.
    const recordIds = new Set([
      ...Object.keys(folderPaths),
      ...(autoSave ? Object.keys(selectedRecords) : []),
    ]);

    recordIds.forEach((recordId) => {
      const record = selectedRecords[recordId];
      if (!record) {
        return;
      }
      const explicitPath = folderPaths[recordId]?.path;
      const defaults = defaultFolderSettings[record.type] || {};
      const defaultPath = isEmailFile ? defaults.saveEmailFolderPath : defaults.saveAttachmentsFolderPath;
      const folderPath = (explicitPath !== undefined && explicitPath !== null) ? explicitPath : defaultPath;
      candidates.push({
        name: file.name,
        fileType: file.contentType || undefined,
        fileSize: typeof file.size === 'number' ? file.size : undefined,
        folderPath: folderPath || '[]',
        relatedToId: recordId,
        relatedToApiName: record.type,
      });
      fileIds.push(file.id);
    });
  });

  return { candidates, fileIds };
};

/**
 * Runs the dry-run validation and dispatches the per-file error map. Fails closed: on error every
 * candidate file is flagged so the upload stays blocked until validation can succeed.
 *
 * A stale-guard callback prevents an older (slower) response from overwriting the state produced by
 * a newer request when the user changes file names/locations in quick succession.
 * @param {Array<object>} files - docrioFiles
 * @param {object} selectedRecords - selectedRecords map
 * @param {string} ownerEmail - owner email address
 * @param {Function} dispatch - redux dispatch
 * @param {Function} [isCurrent] - returns false if a newer validation run has since started
 * @param {object} [options] - auto-save context forwarded to buildFileValidationCandidates
 * @return {Promise<Object.<string, string[]>>} the per-file error map this run produced
 */
export const runFileValidation = async (files, selectedRecords, ownerEmail, dispatch, isCurrent = () => true, options = {}) => {
  const { candidates, fileIds } = buildFileValidationCandidates(files, selectedRecords, options);

  if (candidates.length === 0) {
    if (isCurrent()) {
      dispatch(setFileValidationErrors({}));
      dispatch(setFileValidationPending(false));
    }
    return {};
  }

  try {
    const violations = await validateFiles(candidates, ownerEmail);
    const errorsByFileId = {};
    violations.forEach((violation) => {
      const fileId = fileIds[violation.index];
      if (!fileId) {
        return;
      }
      // The same rule can be reported once per save location for a file, so de-duplicate the
      // messages shown for each row.
      errorsByFileId[fileId] = [...new Set([
        ...(errorsByFileId[fileId] || []),
        ...(violation.messages || []),
      ])];
    });
    if (isCurrent()) {
      dispatch(setFileValidationErrors(errorsByFileId));
    }
    return errorsByFileId;
  } catch (e) {
    logError(e);
    // Fail closed - flag every candidate file so it is excluded from the upload until validation
    // can run cleanly.
    const errorsByFileId = {};
    fileIds.forEach((fileId) => {
      errorsByFileId[fileId] = ['Unable to validate file uploads. Please try again.'];
    });
    if (isCurrent()) {
      dispatch(setFileValidationErrors(errorsByFileId));
    }
    return errorsByFileId;
  } finally {
    // Only the latest run clears pending; a superseded run leaves it for the newer one to resolve.
    if (isCurrent()) {
      dispatch(setFileValidationPending(false));
    }
  }
};

/**
 * Debounced hook that re-runs the dry-run validation whenever the candidate files or their save
 * locations change (covering both the initial render and inline name edits).
 * @param {Array<object>} files - docrioFiles
 * @param {object} selectedRecords - selectedRecords map
 * @param {string} ownerEmail - owner email address
 * @param {Function} dispatch - redux dispatch
 * @param {object} [options] - auto-save context forwarded to buildFileValidationCandidates
 */
export const useFileValidation = (files, selectedRecords, ownerEmail, dispatch, options = {}) => {
  const { saveAllAttachments = false, saveAllEmails = false, defaultFolderSettings = {}, emailFileIds } = options;
  const emailFileIdsKey = [...(emailFileIds instanceof Set ? emailFileIds : (emailFileIds || []))].sort().join(',');

  // Signature captures everything that influences candidate generation so validation re-runs when
  // any of it changes: file name, folder path, related record + its type, the auto-save settings
  // that decide which records a file uploads to, and the default folder paths those settings use.
  const signature = JSON.stringify({
    files: (files || []).map((file) => ({
      id: file.id,
      name: file.name,
      folderPaths: Object.fromEntries(
          Object.entries(file?.folderPaths || {}).map(([recordId, value]) => [recordId, value?.path]),
      ),
    })),
    selectedRecords: Object.entries(selectedRecords || {})
        .map(([recordId, record]) => [recordId, record?.type])
        .sort((a, b) => (a[0] < b[0] ? -1 : 1)),
    saveAllAttachments,
    saveAllEmails,
    defaultFolderSettings,
    emailFileIds: emailFileIdsKey,
  });

  const filesRef = useRef(files);
  const selectedRecordsRef = useRef(selectedRecords);
  const ownerEmailRef = useRef(ownerEmail);
  const optionsRef = useRef(options);
  const requestIdRef = useRef(0);
  filesRef.current = files;
  selectedRecordsRef.current = selectedRecords;
  ownerEmailRef.current = ownerEmail;
  optionsRef.current = options;

  // Set pending before paint so a Save click right after an edit validates fresh instead of using stale errors.
  useLayoutEffect(() => {
    const { candidates } = buildFileValidationCandidates(filesRef.current, selectedRecordsRef.current, optionsRef.current);
    if (candidates.length > 0) {
      dispatch(setFileValidationPending(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Tag this run so a later run supersedes it; out-of-order responses are then discarded.
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      runFileValidation(
          filesRef.current,
          selectedRecordsRef.current,
          ownerEmailRef.current,
          dispatch,
          () => requestId === requestIdRef.current,
          optionsRef.current,
      );
    }, VALIDATION_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // On unmount, clear state so a validation that was scheduled but never ran doesn't linger.
  useEffect(() => () => {
    dispatch(setFileValidationErrors({}));
    dispatch(setFileValidationPending(false));
  }, [dispatch]);
};
